<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import rutHelpers from 'rut-helpers';
import { serviceCompaniesApi, type ServiceCompany } from '@/api/service-companies';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ServiceCompaniesForm from './service-companies-form.vue';
import ServiceCompaniesDiscardForm from './service-companies-discard-form.vue';
import ServiceCompaniesUndiscardForm from './service-companies-undiscard-form.vue';
import ServiceCompanyAuditUsersList from './service-company-audit-users-list.vue';

const { tableProps } = useResponsiveTable();

const selectedServiceCompany = ref<ServiceCompany | null>(null);
const showForm = ref(false);
const showAuditUsersList = ref(false);
const scope = ref<'active' | 'discarded'>('active');

const {
  data: activeServiceCompanies,
  refetch: refetchActiveServiceCompanies,
  isPending: isLoadingActiveServiceCompanies,
} = useQuery({
  queryKey: ['serviceCompanies', 'active'],
  queryFn: () => serviceCompaniesApi.index('active'),
  initialData: [],
});

const {
  data: discardedServiceCompanies,
  refetch: refetchDiscardedServiceCompanies,
  isPending: isLoadingDiscardedServiceCompanies,
} = useQuery({
  queryKey: ['serviceCompanies', 'discarded'],
  queryFn: () => serviceCompaniesApi.index('discarded'),
  initialData: [],
});

const serviceCompanies = computed(() =>
  (scope.value === 'active' ? activeServiceCompanies.value : discardedServiceCompanies.value),
);

const isLoading = computed(() => isLoadingActiveServiceCompanies.value || isLoadingDiscardedServiceCompanies.value);

const actionSuccess = ref(false);

function openCreateForm() {
  selectedServiceCompany.value = null;
  showForm.value = true;
}

function openServiceCompanyUsersList(serviceCompany: ServiceCompany) {
  selectedServiceCompany.value = serviceCompany;
  showAuditUsersList.value = true;
}

function openEditForm(serviceCompany: ServiceCompany) {
  selectedServiceCompany.value = serviceCompany;
  showForm.value = true;
}

function handleDiscardAndUndiscardSuccess() {
  refetchActiveServiceCompanies();
  refetchDiscardedServiceCompanies();
  actionSuccess.value = true;
}

function handleFormSuccess() {
  actionSuccess.value = true;
}

function handleFormClose() {
  showForm.value = false;
  selectedServiceCompany.value = null;
}

function handleAuditUsersListClose() {
  showAuditUsersList.value = false;
  selectedServiceCompany.value = null;
}

const serviceTypeLabels: Record<string, string> = {
  'temporary_services': 'Servicios Temporales',
  'contractor': 'Contratista',
  'subcontractor': 'Subcontratista',
};

function getServiceTypeLabel(type: string) {
  return serviceTypeLabels[type] || type;
}

const headers = [
  { title: 'Nombre de la Empresa', key: 'taxName' },
  { title: 'RUT', key: 'dni' },
  { title: 'Tipo de Servicio', key: 'serviceType' },
  { title: 'Email', key: 'receiptsEmail' },
  { title: 'Creada', key: 'humanCreatedAt' },
  { title: 'Acciones', key: 'actions', sortable: false },
];
</script>
<template>
  <v-card>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="serviceCompanies"
      :loading="isLoading"
      loading-text="Cargando empresas de servicios..."
    >
      <template #top>
        <v-toolbar
          color="white"
          flat
        >
          <v-toolbar-title class="font-weight-bold">
            Empresas de Servicios
          </v-toolbar-title>
        </v-toolbar>
        <v-toolbar
          color="white"
          class="mx-2 mb-4"
          flat
        >
          <v-tabs
            v-model="scope"
            color="primary"
          >
            <v-tab
              value="active"
              class="rounded-pill text-body-1"
            >
              Activas
            </v-tab>
            <v-tab
              value="discarded"
              class="rounded-pill text-body-1"
            >
              Archivadas
            </v-tab>
          </v-tabs>
          <v-spacer />
          <v-btn
            v-if="scope === 'active'"
            class="mb-2 text-white border bg-primary"
            @click="openCreateForm"
          >
            <v-icon
              start
              icon="mdi-plus"
            />
            Agregar Empresa de Servicios
          </v-btn>
        </v-toolbar>
      </template>

      <template #[`item.serviceType`]="{ item }">
        {{ getServiceTypeLabel(item.serviceType) }}
      </template>

      <template #[`item.receiptsEmail`]="{ item }">
        {{ item.receiptsEmail || '-' }}
      </template>

      <template #[`item.dni`]="{ item }">
        {{ rutHelpers.rutFormat(item.dni) }}
      </template>

      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active'"
          class="d-flex"
        >
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
                variant="text"
                color="warning"
              />
            </template>
            <template #default="{ isActive }">
              <service-companies-discard-form
                :service-company="item"
                @close="isActive.value = false"
                @success="handleDiscardAndUndiscardSuccess"
              />
            </template>
          </v-dialog>
          <v-btn
            icon="mdi-account-multiple"
            size="small"
            variant="text"
            color="primary"
            @click="openServiceCompanyUsersList(item)"
          />
        </div>
        <v-dialog
          v-else-if="scope === 'discarded'"
          max-width="600"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-bind="activatorProps"
              icon="mdi-package-up"
              size="small"
              variant="text"
              color="success"
            />
          </template>
          <template #default="{ isActive }">
            <service-companies-undiscard-form
              :service-company="item"
              @close="isActive.value = false"
              @success="handleDiscardAndUndiscardSuccess"
            />
          </template>
        </v-dialog>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showForm"
      max-width="600"
      scrollable
    >
      <service-companies-form
        :service-company="selectedServiceCompany"
        @close="handleFormClose"
        @success="handleFormSuccess"
      />
    </v-dialog>

    <v-dialog
      v-model="showAuditUsersList"
      max-width="600"
      scrollable
    >
      <service-company-audit-users-list
        v-if="selectedServiceCompany"
        :service-company-id="selectedServiceCompany.id"
        @close="handleAuditUsersListClose"
      />
    </v-dialog>
  </v-card>

  <v-snackbar
    v-model="actionSuccess"
    :timeout="2000"
    color="success"
    rounded="pill"
  >
    <v-icon
      icon="mdi-check-circle"
      class="mr-4"
    />
    Cambios guardados exitosamente!
  </v-snackbar>
</template>
